/**
 * Mock data for the informational intent preview.
 *
 * ONE session, FOUR entries — demonstrates N-per-session. Gated behind
 * `?learningMock=1`. Each entry is a self-contained learning with its own
 * kind, turns, detail zone, good-to-know, and sources.
 *
 * PAYLOAD SHAPE MATCHES THE REAL API: `entries` is a SIBLING of `trail`,
 * NOT nested inside it. Per-entry `applies_to`, `prerequisites`, `caveats`
 * are flat fields on the entry (no nested `good_to_know` wrapper).
 */

/* ── shared diagnostic-compat stubs ─────────────────────────────────── */
const DIAG_STUBS = {
  problem_definition: "",
  symptom_text: "",
  symptoms: [],
  facts: [],
  possible_causes_active: [],
  possible_causes_ruled_out: [],
  possible_causes: "",
  root_cause: "",
  root_cause_nid: "",
  resolution: "",
};

export const INFORMATIONAL_TRAIL = {
  id: "mock-info-001",
  session_pk: "mock-info-001",
  intent: "informational",
  status: "draft",
  product: "ZIA / ZCC",
  modes: ["fast"],
  ...DIAG_STUBS,

  // ── session-level synthesis ──
  about: "Z-Tunnel 2.0 transport selection and App Profile configuration",
  key_takeaways: [
    "Transport is an App Profile setting, not a per-client toggle",
    "Use TLS only where UDP 443 is blocked or throttled",
    "App Profile changes apply on the next ZCC reconnect",
    "ZCC 4.3 supports split DNS, with conditions",
  ],
  applies_to: ["Z-Tunnel 2.0", "Zscaler Client Connector 4.3", "ZIA Admin Portal"],
  valid_as_of: "ZCC 4.3.5 · Sep 2026",
  watch_out: ["TLS costs latency", "split DNS needs ZCC 4.3+"],
  still_open: ["Whether split DNS works with a custom PAC"],
  // NOTE: `entries` is NOT here — it is a sibling in the payload.
};

/** Entries array — sibling of `trail` in the real API response. */
export const INFORMATIONAL_ENTRIES = [
  /* ── 1. HOW-TO ──────────────────────────────────────────────────── */
  {
    id: "e1",
    title: "Z-Tunnel 2.0 transport — switching DTLS to TLS",
    kind: "how_to",
    turns: [1, 2],
    lead:
      "Transport is an App Profile setting, not a per-client toggle. " +
      "Move the user to a profile configured for TLS.",
    asked_as: "How do I switch a user from Z-Tunnel 2.0 DTLS to TLS?",
    procedure: [
      { n: 1, action: "Open the App Profile assigned to the user in the ZIA Admin Portal" },
      { n: 2, action: "Set the Z-Tunnel 2.0 transport to TLS instead of DTLS" },
      { n: 3, action: "Have the user reconnect ZCC and confirm the tunnel reports TLS", note: "Expect a brief disconnect" },
    ],
    verification: ["ZCC tunnel status shows TLS in the connection details"],
    key_points: [],
    options: [],
    applies_to: ["Z-Tunnel 2.0", "Zscaler Client Connector"],
    prerequisites: ["Admin access to the ZIA Admin Portal App Profile configuration"],
    caveats: [
      "TLS over TCP 443 is normally slower than DTLS over UDP 443 — prefer it only where UDP 443 is blocked or throttled by the ISP",
    ],
    sources: [
      { kind: "runbook", ref: "ZIAZCC_WA1", title: "Move user to App Profile with ZTunnel 2.0 TLS" },
    ],
    corrected: {
      turn: 6,
      was: "You can toggle DTLS/TLS per user in the ZCC client settings.",
      why: "The original answer implied a client-side toggle exists; it does not. The setting lives on the App Profile.",
    },
  },

  /* ── 2. CONCEPT ─────────────────────────────────────────────────── */
  {
    id: "e2",
    title: "What an App Profile actually controls",
    kind: "concept",
    turns: [3],
    lead:
      "An App Profile binds the transport protocol, PAC file, and forwarding " +
      "profile to a user group. Everything in it applies per-profile, not per-client.",
    asked_as: "What does an App Profile control?",
    procedure: [],
    verification: [],
    key_points: [
      "An App Profile binds transport, PAC, and forwarding profile to a user group",
      "Transport (DTLS vs TLS) is per-profile, not per-client — there is no client-side toggle",
      "Changes apply on the next ZCC reconnect; no reboot needed",
    ],
    options: [],
    applies_to: ["ZIA Admin Portal", "Zscaler Client Connector"],
    prerequisites: [],
    caveats: [],
    sources: [
      { kind: "doc", ref: "ZIA_APPPROFILE", title: "App Profile configuration guide" },
    ],
  },

  /* ── 3. COMPARISON ──────────────────────────────────────────────── */
  {
    id: "e3",
    title: "Choosing a transport for a branch office",
    kind: "comparison",
    turns: [4],
    lead:
      "It depends on the network conditions. DTLS is faster on clean paths; " +
      "TLS is more reliable where the ISP throttles or blocks UDP.",
    asked_as: "Should this branch use DTLS or TLS for Z-Tunnel 2.0?",
    procedure: [],
    verification: [],
    key_points: [
      "Both protocols encrypt identically — the security posture does not change",
      "The choice is a latency-vs-reliability trade-off driven by the ISP",
    ],
    options: [
      {
        option: "DTLS (UDP 443)",
        when_to_use: "UDP 443 is open and packet loss is low (<2%)",
        tradeoff: "Fails silently where ISPs throttle or block UDP — tunnels drop with no clear error",
      },
      {
        option: "TLS (TCP 443)",
        when_to_use: "UDP 443 is blocked or throttled by the ISP, or packet loss is high",
        tradeoff: "Higher latency due to TCP head-of-line blocking; connection setup is slower",
      },
    ],
    applies_to: ["Z-Tunnel 2.0"],
    prerequisites: [],
    caveats: [
      "Some ISPs throttle UDP 443 intermittently — test at peak hours before committing to DTLS",
    ],
    sources: [
      { kind: "kg", ref: "ZT2_DTLS_PERF", title: "Z-Tunnel 2.0 DTLS vs TLS performance" },
    ],
  },

  /* ── 4. CAPABILITY ──────────────────────────────────────────────── */
  {
    id: "e4",
    title: "Does ZCC 4.3 support split DNS",
    kind: "capability",
    turns: [5],
    lead:
      "Yes, but only when the DNS gateway is enabled in the forwarding profile " +
      "and the split-DNS domain list is configured in the App Profile.",
    asked_as: "Does ZCC 4.3 support split DNS?",
    procedure: [],
    verification: [],
    key_points: [
      "Split DNS requires both the DNS gateway toggle and a domain list in the App Profile",
      "Queries for listed domains go through ZIA; everything else uses the local resolver",
    ],
    options: [],
    applies_to: ["Zscaler Client Connector 4.3+"],
    prerequisites: [],
    caveats: [
      "The domain list is static — wildcard entries are supported but regex is not",
    ],
    sources: [],
  },
];

/**
 * Build the mock result object that LearningMode.applyResult expects.
 * `entries` is a sibling of `trail`, matching the real API payload shape.
 */
export function buildInformationalMock() {
  return {
    trail: { ...INFORMATIONAL_TRAIL, entries: INFORMATIONAL_ENTRIES },
    entries: INFORMATIONAL_ENTRIES,
    steps: [],
    hybrid: [],
    corrections: [],
  };
}
